import React from 'react';
import { Check, Loader2, AlertCircle, Clock, Sparkles, Wand2, Film, CircleCheck, Image as ImageIcon } from 'lucide-react';
import type { HyperDuskImageGenerationStatus, HyperDuskOutputMode, HyperDuskProjectStatus } from '../types';

const STEPS = [
  { key: 'queued', label: 'Queued', description: 'Your project is in the processing queue', icon: Clock },
  { key: 'generating_clips', label: 'Generating Dusk Preview', description: 'AI is creating the dusk version', icon: Sparkles },
  { key: 'awaiting_approval', label: 'Awaiting Approval', description: 'Review the night version before triggering animation', icon: Wand2 },
  { key: 'rendering', label: 'Rendering', description: 'Composing your final video', icon: Film },
  { key: 'completed', label: 'Completed', description: 'Your video is ready', icon: CircleCheck },
] as const;

// Image-only projects skip animation/render entirely — the deliverable is
// the generated evening still, so the tracker is a shorter 3-step flow.
const IMAGE_STEPS = [
  { key: 'queued', label: 'Queued', description: 'Your project is in the processing queue', icon: Clock },
  { key: 'generating_clips', label: 'Generating Evening Image', description: 'AI is creating the dusk version', icon: ImageIcon },
  { key: 'awaiting_approval', label: 'Review & Save', description: 'Pick a variant, download, and save', icon: Wand2 },
  { key: 'completed', label: 'Completed', description: 'Your evening image is ready', icon: CircleCheck },
] as const;

interface StatusTrackerProps {
  status: HyperDuskProjectStatus;
  errorMessage?: string | null;
  generationStatus?: HyperDuskImageGenerationStatus | null;
  outputMode?: HyperDuskOutputMode;
}

export default function StatusTracker({
  status,
  errorMessage,
  generationStatus = null,
  outputMode = 'video',
}: StatusTrackerProps) {
  const isImageMode = outputMode === 'image';

  if (isImageMode) {
    let imageActiveIndex: number;
    if (status === 'failed') {
      imageActiveIndex = -1;
    } else if (status === 'completed' || generationStatus === 'completed') {
      imageActiveIndex = IMAGE_STEPS.findIndex((s) => s.key === 'completed');
    } else if (generationStatus === 'awaiting_user_approval' || generationStatus === 'dusk_ready') {
      imageActiveIndex = IMAGE_STEPS.findIndex((s) => s.key === 'awaiting_approval');
    } else if (status === 'generating_clips' || generationStatus === 'dusk_processing') {
      imageActiveIndex = IMAGE_STEPS.findIndex((s) => s.key === 'generating_clips');
    } else {
      imageActiveIndex = IMAGE_STEPS.findIndex((s) => s.key === 'queued');
    }

    return (
      <StepList steps={IMAGE_STEPS} activeIndex={imageActiveIndex} status={status} errorMessage={errorMessage} />
    );
  }

  const steps = STEPS.map((step) => {
    if (step.key === 'generating_clips') {
      return {
        ...step,
        label: 'Generating Dusk Preview',
        description: 'Creating the night version with AI',
      };
    }
    if (step.key === 'awaiting_approval') {
      if (generationStatus === 'dusk_ready' || generationStatus === 'animation_ready') {
        return { ...step, label: 'Approved', description: 'Night version approved — ready for animation' };
      }
      return step;
    }
    if (step.key === 'rendering') {
      if (generationStatus === 'dusk_ready') {
        return { ...step, label: 'Generate Animation', description: 'Choose duration and motion style below' };
      }
      if (generationStatus === 'kling_processing') {
        return { ...step, label: 'Generating Animation', description: 'Creating before/after timelapse' };
      }
      if (generationStatus === 'animation_ready') {
        return { ...step, label: 'Animation Ready', description: 'Clip is ready for final render' };
      }
      return step;
    }
    return step;
  });

  const isFailed = status === 'failed';
  let activeIndex: number;
  if (isFailed) {
    activeIndex = -1;
  } else if (status === 'completed') {
    activeIndex = steps.findIndex((s) => s.key === 'completed');
  } else if (status === 'rendering' || generationStatus === 'kling_processing') {
    activeIndex = steps.findIndex((s) => s.key === 'rendering');
  } else if (generationStatus === 'dusk_ready' || generationStatus === 'animation_ready') {
    activeIndex = steps.findIndex((s) => s.key === 'rendering');
  } else if (generationStatus === 'awaiting_user_approval') {
    activeIndex = steps.findIndex((s) => s.key === 'awaiting_approval');
  } else if (status === 'generating_clips' || generationStatus === 'dusk_processing') {
    activeIndex = steps.findIndex((s) => s.key === 'generating_clips');
  } else if (status === 'queued') {
    activeIndex = steps.findIndex((s) => s.key === 'queued');
  } else {
    activeIndex = steps.findIndex((s) => s.key === status) ?? 0;
  }

  return <StepList steps={steps} activeIndex={activeIndex} status={status} errorMessage={errorMessage} />;
}

interface TrackerStep {
  key: string;
  label: string;
  description: string;
  icon: typeof Clock;
}

function StepList({
  steps,
  activeIndex,
  status,
  errorMessage,
}: {
  steps: readonly TrackerStep[];
  activeIndex: number;
  status: HyperDuskProjectStatus;
  errorMessage?: string | null;
}) {
  const isFailed = status === 'failed';
  return (
    <div>
      <div className="pl-1">
        {steps.map((step, i) => {
          const isComplete = activeIndex > i || (status === 'completed' && activeIndex === i);
          const isActive = activeIndex === i && status !== 'completed';
          const isLast = i === steps.length - 1;
          const StepIcon = step.icon;

          return (
            <div key={step.key} className="flex gap-4">
              <div className="flex flex-col items-center w-9 shrink-0">
                <div className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isComplete
                    ? 'bg-green-600/20 border-2 border-green-500/40'
                    : isActive
                    ? 'bg-blue-600/20 border-2 border-blue-500/50 shadow-lg shadow-blue-500/20'
                    : 'bg-slate-800/80 border-2 border-slate-700/50'
                }`}>
                  {isComplete ? (
                    <Check size={16} className="text-green-400" />
                  ) : isActive ? (
                    <StepIcon size={16} className="text-blue-400 animate-pulse" />
                  ) : (
                    <StepIcon size={14} className="text-slate-600" />
                  )}
                </div>
                {!isLast && (
                  <div className={`w-0.5 flex-1 my-1 transition-colors duration-500 ${
                    isComplete ? 'bg-green-500/40' : 'bg-slate-800'
                  }`} />
                )}
              </div>

              <div className={`flex-1 pb-4 pt-1 ${!isActive && !isComplete ? 'opacity-35' : ''}`}>
                <div className={`rounded-xl p-3 transition-all duration-500 ${
                  isActive
                    ? 'bg-blue-950/40 border border-blue-500/30'
                    : ''
                }`}>
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-semibold ${
                      isActive ? 'text-white' : isComplete ? 'text-slate-300' : 'text-slate-500'
                    }`}>
                      {step.label}
                    </p>
                    {isActive && (
                      <Loader2 size={14} className="text-blue-400 animate-spin" />
                    )}
                    {isComplete && (
                      <span className="text-[10px] text-green-400 font-medium bg-green-500/10 px-1.5 py-0.5 rounded">Done</span>
                    )}
                  </div>
                  <p className={`text-xs mt-0.5 ${isActive ? 'text-slate-400' : 'text-slate-600'}`}>
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isFailed && errorMessage && (
        <div className="mt-4 p-4 rounded-xl bg-red-900/15 border border-red-500/20">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-300">Processing failed</p>
              <p className="text-sm text-red-400/70 mt-1">{errorMessage}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
